import { Flex, Table } from "@mantine/core";
import { useTranslation } from "react-i18next";

interface RAPDTablesProps {
  leftResultsDirect: any;
  leftResultsConsensual: any;
  rightResultsDirect: any;
  rightResultsConsensual: any;
}

export default function RAPDTables(props: RAPDTablesProps) {
  const { leftResultsDirect, leftResultsConsensual, rightResultsDirect, rightResultsConsensual } =
    props;
  const { t } = useTranslation();

  const formatValue = (value, unit) => {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return "—";
    }

    return `${value.toFixed(2)} ${unit || ""}`.trim();
  };

  // Helper function to ensure we have an array of results
  const ensureArray = (results) => {
    if (!results) return [];
    if (Array.isArray(results)) return results;

    // If it's a single object with properties we care about
    if (typeof results === "object") {
      // Convert object to array of {label, value, unit} items
      return Object.entries(results)
        .filter(([key, value]) => key !== "baselinePupilSize" && value !== undefined)
        .map(([key, value]) => ({
          label: key,
          value: typeof value === "number" && Number.isFinite(value) ? value : null,
          unit: key.includes("time") ? "s" : "%",
        }));
    }

    return [];
  };

  const table = (directResults, consensualResults, eye) => {
    // Convert inputs to arrays if needed
    const directArray = ensureArray(directResults);
    const consensualArray = ensureArray(consensualResults);

    // Make sure we have data to display
    if (directArray.length === 0 || consensualArray.length === 0) {
      return (
        <Table variant="vertical" layout="fixed" withTableBorder>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td>{t("loading")}</Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      );
    }

    // Create combined table with three columns
    return (
      <Table variant="vertical" layout="fixed" withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th w={160}>{t(`extraTests.pupillometry.${eye}Mean`)}</Table.Th>
            <Table.Th>{t("extraTests.pupillometry.direct")}</Table.Th>
            <Table.Th>{t("extraTests.pupillometry.consensual")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {directArray.map((directItem, index) => {
            // Find matching consensual item
            const consensualItem = consensualArray.find((item) => item.label === directItem.label);

            return (
              <Table.Tr key={index}>
                <Table.Th w={160}>{t(`extraTests.pupillometry.${directItem.label}`)}</Table.Th>
                <Table.Td>{formatValue(directItem.value, directItem.unit)}</Table.Td>
                <Table.Td>{formatValue(consensualItem?.value, consensualItem?.unit)}</Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    );
  };

  return (
    <Flex justify="space-between" align="flex-start" gap="md" my="md">
      {table(leftResultsDirect, leftResultsConsensual, "leftEye")}
      {table(rightResultsDirect, rightResultsConsensual, "rightEye")}
    </Flex>
  );
}
